/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMPublishProfileComponent" />
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ISubscriptionContextValue, Subscription, SubscriptionConsumer, SubscriptionPicker, SubscriptionProvider } from "DistributedTask/Subscriptions/SubscriptionPicker";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { AzureRMActionCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Actions/AzureRMActionCreator";
import { AzureRMKeys, Environment } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/AzureRMEndpointConstants";
import { AzureRMStore, IAzureRMStoreState } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Store/AzureRMStore";
import * as React from "react";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { AzureSubscription, ServiceEndpoint, AzureAppService, AccessTokenRequestType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface IAzureRMPublishProfileComponentProps extends IEndpointEditorComponentProps {
    updateValidAndDirtyState: (isValid?: boolean, idDirty?: boolean) => void;
    updateInvalidComponentCount: (invalidComponentCount: number) => void;
}

export interface IAzureRMPublishProfileComponentState extends IAzureRMStoreState {
    selectedSubscription: AzureSubscription;
    selectedResourceGroup: IListBoxItem;
    selectedAppServiceAndSlot: AzureAppService;
    isSubscriptionsLoaded: boolean;
    isComponentValid: boolean;
}

export class AzureRMPublishProfileComponent extends VssComponent<IAzureRMPublishProfileComponentProps, IAzureRMPublishProfileComponentState> {
    constructor(props: IAzureRMPublishProfileComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._draftServiceEndpoint = Object.assign({}, props.draftEndpoint);
        this._draftServiceEndpoint.data = this._draftServiceEndpoint.data || {};
        this._draftServiceEndpoint.authorization.parameters = this._draftServiceEndpoint.authorization.parameters || {};

        this._actionCreator = ActionCreatorManager.GetActionCreator<AzureRMActionCreator>(AzureRMActionCreator, AzureRMKeys.PUBLISH_PROFILE_INSTANCE_KEY);
        this._store = StoreManager.GetStore<AzureRMStore>(AzureRMStore, AzureRMKeys.PUBLISH_PROFILE_INSTANCE_KEY);
        this._store.addChangedListener(this._onStoreChange);
  
        this._initializeValues();
        this.setTimeout(() => {
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        });
    }

    public componentWillReceiveProps(props: IAzureRMPublishProfileComponentProps) {
        this._draftServiceEndpoint = Object.assign({}, props.draftEndpoint);
    }

    public componentWillUnmount() {
        if (!this.state.isComponentValid) {
            this.props.updateValidAndDirtyState(true);
        }
        this._store.removeChangedListener(this._onStoreChange);
    }
    
    public render(): React.ReactNode {
        let isEditable = true;
        if (this.props.isUpdateForm) {
            isEditable = false;
        }

        let subscriptionComponent: React.ReactFragment;
        if (isEditable) {
            subscriptionComponent = <SubscriptionProvider pageContext={this.context.pageContext}>
                                        <SubscriptionConsumer>
                                            {(subscriptionContext: ISubscriptionContextValue) => {
                                                const isLoadComplete = subscriptionContext.state && subscriptionContext.state.loadedAllSubscriptions;
                                                const subscriptions: Subscription[] | undefined = isLoadComplete ? subscriptionContext.state.subscriptions : undefined;
                                                this._performSubscriptionSpecificOperations(isLoadComplete, subscriptions);
                                                return (
                                                    <SubscriptionPicker
                                                        disabled={!isEditable}
                                                        useDropdown={true}
                                                        label={Resources.Subscription}
                                                        showCreateSubscriptionButton={false}
                                                        subscriptionContext={subscriptionContext}
                                                        onSubscriptionChoiceChanged={this._onSubscriptionChanged}
                                                        selectedSubscriptionId={this.state.selectedSubscription && this.state.selectedSubscription.subscriptionId || ""}
                                                        className="default-input-margin-top"
                                                    />);
                                            }}
                                        </SubscriptionConsumer>
                                    </SubscriptionProvider>;
        } else {
            subscriptionComponent = <FormItem
                                        label={Resources.Subscription}
                                        className="default-input-margin-top">
                                        <TextField
                                            value={this.state.selectedSubscription && this.state.selectedSubscription.displayName + " (" + this.state.selectedSubscription.subscriptionId + ")"}
                                            disabled={true}
                                        />
                                    </FormItem>;
        }
        return  (<React.Fragment>
                    {subscriptionComponent}
                    {this.state.selectedSubscription && (!isEditable || this.state.isSubscriptionsLoaded) && this._getDropdownForResourceGroup()}
                    {this.state.selectedSubscription && (!isEditable || this.state.isSubscriptionsLoaded) && this._getDropdownForAppService()}
                </React.Fragment>);
    }

    private _performSubscriptionSpecificOperations = (isLoadComplete: boolean, subscriptions: Subscription[]): void => {
        setTimeout( () => {
            if (!isLoadComplete) {
                    this.props.updateInvalidComponentCount(1); // for disabling the save button until subscriptions are loaded
            }
            else if (!this.state.isSubscriptionsLoaded) {
                    this._onSubscriptionsLoaded();
            }
    
            if (!subscriptions || subscriptions.length === 0) {
                    this.props.updateInvalidComponentCount(1); // for disabling the save button if no subscriptions are available
            }
            else if (!this.state.selectedSubscription && subscriptions && subscriptions[0]) {
                    this._onSubscriptionChanged(subscriptions[0]);
            } else if (this.state.selectedSubscription) {
                if (this.state.selectedResourceGroup && this.state.selectedAppServiceAndSlot) {
                    this.props.updateInvalidComponentCount(0); // for enabling the save button only after resource group and webapp are selected
                } else if (this.state.selectedResourceGroup) {
                    this.props.updateInvalidComponentCount(1);
                } else {
                    this.props.updateInvalidComponentCount(2);
                }
            }
        });
    }

    private _getDropdownForResourceGroup(): JSX.Element {
        const label: string = Resources.ResourceGroup;
        const values: IListBoxItem[] = this._getResourceGroups();
        let selectedValue: IListBoxItem = this.state.selectedResourceGroup && {id: this.state.selectedResourceGroup.id, text: this.state.selectedResourceGroup.id};
        if (!this._dropdownSelectionDictionary[label]) {
            this._dropdownSelectionDictionary[label] =  new DropdownSelection();
        }

        if (this.state.fetchingMoreResourceGroups) {
            return this._renderLoadingComponent(label, label, true, true);
        }

        this._updateDropdownDictionary(label, values, selectedValue);

        return (
            <FormItem
                label={label}
                className="default-input-margin-top"
            >
                <Dropdown
                    // disabled={isDisabled}
                    disabled={false} // resource groups is always editable for subscription 
                    ariaLabel={label}
                    key={label}
                    items={values}
                    selection={this._dropdownSelectionDictionary[label]}
                    onSelect={this._onResourceGroupChanged}
                    onExpand={this._onResourceGroupMenuOpened}
                    filterThrottleWait={200}
                />
            </FormItem>
        );
    }

    private _getDropdownForAppService(): JSX.Element {
        const label: string = Resources.WebApp;
        const values: IListBoxItem[] = this._getAppServiceAndSlot();
        const selectedValue: IListBoxItem = this.state.selectedAppServiceAndSlot && {id: this.state.selectedAppServiceAndSlot.id, text: this.state.selectedAppServiceAndSlot.name};
        if (!this._dropdownSelectionDictionary[label]) {
            this._dropdownSelectionDictionary[label] =  new DropdownSelection();
        }

        if (this.state.fetchingMoreAppServiceAndSlot) {
            return this._renderLoadingComponent(label, label, true, true);
        }

        this._updateDropdownDictionary(label, values, selectedValue);

        return (
            <FormItem
                label={label}
                className="default-input-margin-top"
            >
                <Dropdown
                    disabled={false} // editable in update flow as well
                    ariaLabel={label}
                    key={label}
                    items={values}
                    selection={this._dropdownSelectionDictionary[label]}
                    onSelect={(event: React.SyntheticEvent<HTMLElement>, appServiceAndSlot: IListBoxItem): void => {
                        this._onAppServiceAndSlotChanged(appServiceAndSlot);
                    }}
                    onExpand={this._onAppServiceAndSlotMenuOpened}
                />
            </FormItem>
        );
    }

    private _updateDropdownDictionary = (label: string, values: IListBoxItem[], selectedValue: IListBoxItem): void => {
        if (values.length > 0) {
            if (selectedValue) {
                values.forEach((value, index) => {
                    if (value.id === selectedValue.id) {
                        this._dropdownSelectionDictionary[label].select(index);
                    }
                });
            } else {
                this._dropdownSelectionDictionary[label].clear();
            }
        } else {
            if (selectedValue) {
                values.push(selectedValue);
                this._dropdownSelectionDictionary[label].select(0);
            } else {
                if (this._dropdownSelectionDictionary[label].selectedCount > 0) {
                    this._dropdownSelectionDictionary[label].clear();
                }
            }
        }
    }

    private _renderLoadingComponent = (label: string, key: string, isEditable: boolean, showSpinner: boolean): JSX.Element => {
        const loadingItem = { id: "loading", type: ListBoxItemType.Loading };
            let options: IListBoxItem[] = [];
            options.push(loadingItem);
            this._dropdownSelectionDictionary[label].clear();
            return (
                        <FormItem
                            label = {label}
                            className="default-input-margin-top"
                        >
                            <Dropdown
                                disabled={!isEditable}
                                items={options}
                                key={key}
                                loading={showSpinner}
                            />
                        </FormItem>
                    );
    }

    private _onSubscriptionsLoaded = () => {
        this.setState({ isSubscriptionsLoaded: true });
    }

    private _onSubscriptionChanged = (subscription: Subscription | undefined): void => {
        const azureSubscription = subscription
            ? {
                displayName: subscription.displayName,
                subscriptionId: subscription.id,
                subscriptionTenantId: subscription.subscriptionAccount.subscriptionTenantId,
            } as AzureSubscription
            : undefined;

        this._onAzureSubscriptionChanged(azureSubscription);
    }

    private _onAzureSubscriptionChanged(azureSubscription: AzureSubscription) {
        if (azureSubscription) {
            this._actionCreator.updateResourceGroups([]);
            this.setState({
                 selectedSubscription: azureSubscription, 
                 isComponentValid: true, 
                 selectedResourceGroup: null, 
                }, () => {
                    if (!this.props.isUpdateForm) {
                        this._resetCommonPropertiesDraftEndpoint();
                    }
                    this._draftServiceEndpoint.data.subscriptionName = azureSubscription.displayName;
                    this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                    if (this.state.isComponentValid !== this._isComponentValid) {
                        this._isComponentValid = this.state.isComponentValid;
                    }
                    this.props.updateInvalidComponentCount(2);
                });
        }
    }

    private _getResourceGroups(): IListBoxItem[] {
        let options: IListBoxItem[] = [];
        let rgList = this.state.resourceGroups;
        if (rgList) {
            rgList.forEach(rg => {
                options.push({
                    id: this._getKey(rg),
                    text: rg,
                    data: rg
                });
            });
        }
        return options;
    }

    private _getAppServiceAndSlot(): IListBoxItem[] {
        let options: IListBoxItem[] = [];
        let appServiceList = this.state.appServiceAndSlots;
        if (appServiceList) {
            appServiceList.forEach(appService => {
                options.push({
                    id: appService.id,
                    text: appService.name,
                    data: appService
                });
            });
        }
        return options;
    }

    private _onResourceGroupChanged = (event: React.SyntheticEvent<HTMLElement>, resourceGroup: IListBoxItem) => {
        this.setState({
            selectedResourceGroup: resourceGroup, 
            appServiceAndSlots: [],
            fetchingMoreAppServiceAndSlot: true,
            selectedAppServiceAndSlot: null,
            isComponentValid: false
        }, () => {
            this._updateDraftEndpointStateForResourceGroup();
            this._actionCreator.getAzureAppServiceAndSlot(this.state.selectedSubscription, "", this._draftServiceEndpoint.authorization.parameters.authenticationType, EndpointConstants.AzureEndpointConstants.SpnCreationModeAutomatic, "Subscription", resourceGroup.text);
            if (this.state.isComponentValid !== this._isComponentValid) {
                this._isComponentValid = this.state.isComponentValid;
            }
            this.props.updateInvalidComponentCount(1);
        });
    }

    private _updateDraftEndpointStateForResourceGroup = () => {
        if (!this.props.isUpdateForm) {
            this._resetCommonPropertiesDraftEndpoint();
        }

        this._draftServiceEndpoint.data.subscriptionName = this.state.selectedSubscription.displayName;

        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _onResourceGroupMenuOpened = () => {
        if (!this.state.resourceGroups || this.state.resourceGroups.length === 0) {
            this._actionCreator.getAccessToken(this.state.selectedSubscription.subscriptionTenantId);
            this._actionCreator.getAzureRMResourceGroups(this.state.selectedSubscription, "", 
                this._draftServiceEndpoint.authorization.parameters.authenticationType, 
                EndpointConstants.AzureEndpointConstants.SpnCreationModeAutomatic, "Subscription");
        } else {
            this.setState({});
        }
    }

    private _onAppServiceAndSlotMenuOpened = () => {
        if ((!this.state.appServiceAndSlots || this.state.appServiceAndSlots.length === 0) && this.state.selectedResourceGroup) {
            this._onResourceGroupChanged(null, this.state.selectedResourceGroup);
        }
    }

    private _onAppServiceAndSlotChanged = (appServiceAndSlot: IListBoxItem) => {
        let appServiceAndSlotList = this.state.appServiceAndSlots;
        const appService: AzureAppService = appServiceAndSlotList.find((_appServiceAndSlot) => { return _appServiceAndSlot.id === appServiceAndSlot.id; });
        if (appService) {
            this.setState({
                selectedAppServiceAndSlot: appService,
                isComponentValid: true
            }, () => {
                this._updateDraftEndpointStateForAppServiceAndSlot();
                if (this.state.isComponentValid !== this._isComponentValid) {
                    this._isComponentValid = this.state.isComponentValid;
                }
                this.props.updateInvalidComponentCount(0);
            });
        }
    }

    private _updateDraftEndpointStateForAppServiceAndSlot = () => {
        if (!this.props.isUpdateForm) {
            this._resetCommonPropertiesDraftEndpoint();
        }
        this._draftServiceEndpoint.data.resourceId = this.state.selectedAppServiceAndSlot.id;
        this._draftServiceEndpoint.data.subscriptionName = this.state.selectedSubscription.displayName;
        this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedSubscription.subscriptionTenantId;

        if (!!this.state.accessToken) {
            this._draftServiceEndpoint.authorization.parameters.accessTokenFetchingMethod = AccessTokenRequestType.Oauth.toString();
            this._draftServiceEndpoint.authorization.parameters.accessToken = this.state.accessToken;
        }

        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _resetCommonPropertiesDraftEndpoint() {
        this._draftServiceEndpoint.url = this.state.serverUrl;
        this._draftServiceEndpoint.data.environment = Environment.AZURE_CLOUD;
        this._draftServiceEndpoint.data.resourceId = "";
        this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedSubscription && this.state.selectedSubscription.subscriptionTenantId as string;
    }

    private _onStoreChange = (): void => {
        let storeState = this._store.getState();

        this.setState({
            resourceGroups: storeState.resourceGroups,
            accessToken: storeState.accessToken,
            fetchingMoreResourceGroups: storeState.fetchingMoreResourceGroups,
            errorMessage: storeState.errorMessage,
            selectedResourceGroup: this.state.selectedResourceGroup,
            fetchingMoreAppServiceAndSlot: storeState.fetchingMoreAppServiceAndSlot,
            appServiceAndSlots: storeState.appServiceAndSlots,
            selectedAppServiceAndSlot: this.state.selectedAppServiceAndSlot
        });
    }

    private _initializeValues() {
        this._isComponentValid = this.props.isUpdateForm ? true : false;

        if (!this.props.isUpdateForm) {
            this._draftServiceEndpoint.data = {};
            this._draftServiceEndpoint.authorization.parameters = {};
        }
        
        let storeState: IAzureRMStoreState = this._store.getState();

        this.state = {
            selectedSubscription: this._getSubscriptionFromEndpoint(),
            selectedResourceGroup: this._getResourceGroupFromEndpoint(),
            selectedAppServiceAndSlot: this._getAppServiceAndSlotFromEndpoint(),
            isSubscriptionsLoaded: false,
            isComponentValid: this._isComponentValid,

            resourceGroups: storeState.resourceGroups,
            appServiceAndSlots: storeState.appServiceAndSlots,
            accessToken: storeState.accessToken,
            errorMessage: storeState.errorMessage,
            fetchingMoreResourceGroups: storeState.fetchingMoreResourceGroups,
            fetchingMoreAppServiceAndSlot: storeState.fetchingMoreAppServiceAndSlot,
            serverUrl: this._draftServiceEndpoint.url || "",
            managementGroups: null,
            mlWorkspaces: null,
            fetchingManagementGroups: false,
            fetchingMoreMLWorkspaces: false
        };

        this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedSubscription && this.state.selectedSubscription.subscriptionTenantId as string;
    }

    private _getSubscriptionFromEndpoint = (): AzureSubscription => {
        if (this._draftServiceEndpoint && this._draftServiceEndpoint.data.resourceId) {
            let resourceId = this._draftServiceEndpoint.data.resourceId.startsWith("/") 
                ? this._draftServiceEndpoint.data.resourceId : "/" + this._draftServiceEndpoint.data.resourceId;
            let resourceIdSplit = resourceId.split("/");
            if (resourceIdSplit.length < 9) {
                return undefined;
            }

            let subscriptionId = resourceIdSplit[2];

            return ({
                subscriptionId: subscriptionId,
                displayName: this._draftServiceEndpoint.data.subscriptionName || subscriptionId,
                subscriptionTenantId: this._draftServiceEndpoint.authorization.parameters.tenantid || "",
                subscriptionTenantName: ""
            });
        }

        return undefined;
    }

    private _getResourceGroupFromEndpoint = (): IListBoxItem => {
        if (this._draftServiceEndpoint.data && this._draftServiceEndpoint.data.resourceId) {
            let resourceId = this._draftServiceEndpoint.data.resourceId.startsWith("/") 
                ? this._draftServiceEndpoint.data.resourceId : "/" + this._draftServiceEndpoint.data.resourceId;
            let resourceIdSplit = resourceId.split("/");
            if (resourceIdSplit.length < 9) {
                return undefined;
            }
            let name = resourceIdSplit[4];
            return ({
                id: this._getKey(name),
                text: name,
                data: name
            });
        }

        return undefined;
    }

    private _getAppServiceAndSlotFromEndpoint = (): AzureAppService => {
        if (this._draftServiceEndpoint.data && this._draftServiceEndpoint.data.resourceId) {
            let resourceId = this._draftServiceEndpoint.data.resourceId.startsWith("/") ? this._draftServiceEndpoint.data.resourceId : "/" + this._draftServiceEndpoint.data.resourceId;
            let resourceIdSplit = resourceId.split("/");
            if (resourceIdSplit.length < 9) {
                return undefined;
            }
            let name: string;
            if (resourceIdSplit.length === 11) {
                name = resourceIdSplit[8] + "-" + resourceIdSplit[10];
            } else {
                name = resourceIdSplit[8];
            }
            return ({
                id: this._draftServiceEndpoint.data.resourceId,
                name: name
            });
        }

        return undefined;
    }

    private _getKey(val: string): string {
        if (val) {
            return val.replace(" ", "_");
        }
        return "";
    }

    private _isComponentValid: boolean;
    private _draftServiceEndpoint: ServiceEndpoint;
    private _actionCreator: AzureRMActionCreator;
    private _store: AzureRMStore;
    private  _dropdownSelectionDictionary: { [key: string]: DropdownSelection } = {};
}